import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import update from 'immutability-helper';
import {
  packingItemKey,
  packageRow,
  initialPackingPackages,
  assignPackageItems,
} from '../../../../utility/packaging/package-items';
import styled from 'styled-components';
import { Barcode, RectangleDashed, Tray } from '@phosphor-icons/react';
import {
  BodyContainer,
  HeaderText,
  HoverBorderButton,
  ShadowBox,
} from '../../../../elements';
import { PackingDetailHook } from '../../../../hooks/packing';
import {
  CancelledItems,
  CreateNewLabelPopup,
  EditAddressPopup,
  PackageCorrectionPopup,
  PackagingConfirmationPopup,
  PackingDetailHeaderActions,
  PackingDetailHeaderLead,
  PackingDetailItems,
  PackingDetailPacks,
} from '../../../../page-components/packaging';
import { useBarcodeScanner } from '../../../../hooks/barcode';
import { CommonUtility, ErrorMessageConstant, ToastMessage } from '../../../../utility';
import {
  createScannerDiagnostics,
  redactBarcode,
  shouldEnableScannerDiagnostics,
} from '../../../../hooks/barcode-scanner';
import {
  formatPackingItemLabelFileName,
  formatTotePackageLabelFileName,
  itemMatchesCode,
} from '@utility/utils/packing';
import { PackagingService } from '../../../../utility/services/packaging';
import { ConfirmationPopup } from '../../../../components';
import { downloadPDF } from '../../../../page-components/packaging/detail/common';
import { useAuth, useGlobalContext } from '../../../../contexts';
import { PackagingDetailPlaceholder } from '@page-components/packaging/detail/placeholder';
import { ToteBarcodePopup } from '@page-components/packaging/detail/tote-barcode-popup';
import { doesPackageLabelMatchPackedItems } from '@page-components/packaging/detail/generate-pack-labels';
import { useAppHeaderLead } from '../../../../hooks/useAppHeaderLead';
import { useHeaderActions } from '../../../../hooks/useHeaderActions';

const OpenContainerBody = styled(BodyContainer)`
  height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const Pill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 7px;
  padding: 13px 3px;
  border-radius: 15px;
  white-space: nowrap;
  width: fit-content;
  background: ${({ theme }) => theme.colors.box};
`;

const ToteTextContainer = styled.div`
  writing-mode: vertical-rl;
  text-orientation: mixed;
  .rotate {
    transform: rotateZ(90deg);
  }

  .icon {
    min-width: 20px;
  }
`;

const ToteContainer = styled(ShadowBox)`
  width: 65px;
`;

export function PickingDetailScreen() {
  const { containerNumber } = useParams();
  const [items, setItems] = useState([]);
  const [currentScannedUPC, setCurrentScannedUPC] = useState(null);
  const [processing, setProcessing] = useState(false);

  const [searchParams] = useSearchParams();
  const warehouseId = searchParams.get('warehouseId');

  const [showBarcodeModal, setShowBarcodeModal] = useState(false);

  const [openEditAddressPopup, setOpenEditAddressPopup] = useState(false);
  const { currentStation, currentWarehouse } = useGlobalContext();
  const { isUser, user, currentOrg } = useAuth();
  const { data, loading, refresh, error, cancelledItems } = PackingDetailHook(
    containerNumber,
    'code',
    null,
    warehouseId,
  );
  const [shipped, setShipped] = useState(false);

  const navigate = useNavigate();
  const bodyRef = useRef(null);
  // refund popup
  const [labelToRefund, setLabelToRefund] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);
  const [wasPackageLabel, setWasPackageLabel] = useState(false);
  // create label popup
  const [containerItems, setContainerItems] = useState([]);
  const [labelAllocations, setLabelAllocations] = useState([]);
  const [createNewLabelPopup, setCreateNewLabelPopup] = useState(false);
  const [orderToUse, setOrderToUse] = useState(null);
  // add package
  const [addPackagePopup, setAddPackagePopup] = useState(false);
  const [packages, setPackages] = useState([]);
  const [activePackage, setActivePackage] = useState(0);
  const [labelForPackage, setLabelForPackage] = useState(null);
  const [cachedPackage, setCachedPackage] = useState(null);
  const [confirmationPopup, setOpenConfirmationPopup] = useState(false);
  const [packageCorrectionOpen, setPackageCorrectionOpen] = useState(false);
  const [packageCorrectionContext, setPackageCorrectionContext] = useState(null);
  const [confirmationAllocation, setConfirmationAllocation] = useState(null);

  const resetScan = () => {
    setCurrentScannedUPC('');
  };

  const scannerDiagnostics = useMemo(
    () =>
      createScannerDiagnostics({
        enabled: shouldEnableScannerDiagnostics({ currentOrg, user }),
        context: {
          ticket: 'POR-464',
          screen: 'packing-detail',
          containerNumber,
          warehouseId,
          warehouseName: currentWarehouse?.name,
          stationId: currentStation?.stationId,
          stationName: currentStation?.stationName,
          organizationId: currentOrg?._id || user?.organizationId,
          organizationName: currentOrg?.name,
          userId: user?._id,
        },
        upload: payload => PackagingService.uploadScannerDiagnostics(payload),
      }),
    [currentOrg, currentStation, currentWarehouse, containerNumber, user, warehouseId],
  );

  useBarcodeScanner(code => setCurrentScannedUPC(code), {
    disabled: processing || loading || confirmationPopup,
    diagnostics: scannerDiagnostics,
  });

  const key = useMemo(() => (data?.tote ? 'tote' : 'pallet'), [data]);

  useEffect(() => {
    if (!CommonUtility.isValidObject(data?.[key]) || key !== 'tote') return;
    const packages = initialPackingPackages(
      data[key].contents,
      data[key].shipping?.labels,
    );

    if (addPackagePopup) {
      packages.push({ items: [] });
      setActivePackage(packages.length - 1);
      setAddPackagePopup(false);
    }

    setPackages(packages);
  }, [data, key]);

  const packItem = async code => {
    const findRelevantItem = items?.find(x => itemMatchesCode(x, code, 'packed'));

    if (!findRelevantItem) {
      ToastMessage.error('Item not found, try scanning again');
      return;
    }

    setProcessing(true);

    const payload = {
      allocationId: findRelevantItem?.allocationId?._id || findRelevantItem?.allocationId,
    };

    try {
      await PackagingService.packToteItem(data?.[key]?._id, payload);
      resetScan();
      setItems(items =>
        items.map(item => {
          if (item._id === findRelevantItem._id) {
            return {
              ...item,
              status: 'packed',
            };
          }
          return item;
        }),
      );

      setPackages(packages => {
        const findItemIndex = packages[activePackage].items.findIndex(
          x => packingItemKey(x) === packingItemKey(findRelevantItem),
        );

        if (findItemIndex > -1) {
          packages[activePackage].items[findItemIndex].qty += 1;
        } else {
          packages[activePackage].items.push(packageRow(findRelevantItem));
        }
        return packages;
      });
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (CommonUtility.isValidArray(data?.[key]?.contents)) {
      setItems(data?.[key]?.contents);
    } else {
      setItems([]);
    }
  }, [data, key]);

  const allPacked = useMemo(
    () => items?.every(item => item.status === 'packed'),
    [items],
  );

  const isPallet = key === 'pallet';

  const isUnSorted = !!data?.[key]?.unSorted;

  const onFulfillClicked = () => {
    // check if all items are packed
    const allItemsPacked = items?.every(item => item.status === 'packed');

    if (!allItemsPacked) {
      ToastMessage.error('All items must be packed before fulfilling the tote');
      return;
    }

    // Prevent user from configuring Package
    if (isUser) {
      fulfillTote();
    } else {
      openConfirmationPopup();
    }
  };

  useEffect(() => {
    if (!currentScannedUPC) return;

    if (processing || loading || !CommonUtility.isValidObject(data?.[key])) {
      scannerDiagnostics.record('scan-ignored', {
        reason: 'packing-detail-not-ready',
        barcode: redactBarcode(currentScannedUPC),
        processing,
        loading,
        hasContainer: CommonUtility.isValidObject(data?.[key]),
      });
      resetScan();
      return;
    }

    const scannedCode = currentScannedUPC;
    resetScan();

    scannerDiagnostics.record('scan-dispatch', {
      barcode: redactBarcode(scannedCode),
      processing,
      isPallet,
      isUnSorted,
      itemCount: items.length,
      pendingItemCount: items.filter(item =>
        isPallet ? item.status !== 'shipped' : item.status !== 'packed',
      ).length,
    });

    if (isPallet) {
      if (isUnSorted) {
        confirmFulfillItem(scannedCode);
      } else {
        fulfillItem(scannedCode);
      }
    } else {
      const isToteScanned =
        scannedCode?.toLowerCase() === data?.[key]?.toteNumber?.toLowerCase();

      if (isToteScanned) {
        onFulfillClicked();
      } else {
        packItem(scannedCode);
      }
    }
  }, [currentScannedUPC, processing, loading, data, key, isPallet, isUnSorted, items]);

  const confirmFulfillItem = useCallback(
    code => {
      const findRelevantItemIndex = items?.findIndex(x =>
        itemMatchesCode(x, code, 'shipped'),
      );

      if (findRelevantItemIndex < 0) {
        ToastMessage.error('Item not found, try scanning again');
        return;
      }

      const findRelevantItem = items[findRelevantItemIndex];
      setConfirmationAllocation(findRelevantItem);
      setOpenConfirmationPopup(true);
    },
    [items],
  );

  const fulfillItem = async (code, newLabel = null) => {
    const findRelevantItemIndex = items?.findIndex(x =>
      itemMatchesCode(x, code, 'shipped'),
    );

    if (findRelevantItemIndex < 0) {
      ToastMessage.error('Item not found, try scanning again');
      return;
    }

    const findRelevantItem = items[findRelevantItemIndex];

    const payload = {
      palletId: data?.[key]?._id,
      allocations: [
        {
          allocationId: findRelevantItem?.allocationId?._id,
          quantity: 1,
        },
      ],
    };

    try {
      setProcessing(true);
      await PackagingService.shipItem(findRelevantItem.order._id, payload);
      scannerDiagnostics.record('ship-item-success', {
        orderId: findRelevantItem.order._id,
        palletId: data?.[key]?._id,
        allocationId: findRelevantItem?.allocationId?._id,
      });
      resetScan();
      setItems(items =>
        items.map(item => {
          if (item?.allocationId?._id === findRelevantItem?.allocationId?._id) {
            return {
              ...item,
              status: 'shipped',
            };
          }
          return item;
        }),
      );
      onCloseConfirmationPopup();

      const labelToPrint = newLabel || findRelevantItem?.shipping?.labels?.[0];
      const fileName = formatPackingItemLabelFileName(
        findRelevantItem?.order?.marketplace_order_display_id,
        findRelevantItemIndex + 1,
      );

      await askToPrint(labelToPrint?.url, fileName);

      // check if any other item is pending
      const itemLeft = items?.find(
        x =>
          x?.allocationId?._id !== findRelevantItem?.allocationId?._id &&
          x.status !== 'shipped',
      );

      if (!itemLeft) {
        ToastMessage.success('All items shipped successfully');
        scannerDiagnostics.record('navigate-after-scan', {
          reason: 'all-items-shipped',
          from: `/app/fulfill/packing/details/${containerNumber}`,
          to: '/app/fulfill/packing/list/to-pack',
        });
        scannerDiagnostics.flush('packing-detail-navigation', {
          reason: 'all-items-shipped',
        });
        navigate('/app/fulfill/packing/list/to-pack');
      }
    } catch (error) {
      scannerDiagnostics.record('ship-item-error', {
        status: error?.status,
        code: error?.code,
      });
      scannerDiagnostics.flush('ship-item-error');
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  const printItem = (item, index) => {
    const fileName = formatPackingItemLabelFileName(
      item?.order?.marketplace_order_display_id,
      index + 1,
    );
    askToPrint(item?.shipping?.labels?.[0]?.url, fileName);
  };

  const fulfillTote = async () => {
    try {
      setProcessing(true);
      await PackagingService.shipItem(data?.[key]?.order?._id, {
        toteId: data?.[key]?._id,
      });
      setShipped(true);
      ToastMessage.success('Tote shipped successfully');
      onCloseConfirmationPopup();
      await print();
      navigate('/app/fulfill/packing/list/to-pack');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  const openConfirmationPopup = () => {
    setOpenConfirmationPopup(true);
  };

  const unPackItem = async (item, packageNumber = 0) => {
    const findRelevantItem = items?.find(
      x => x?.allocationId?._id === item.allocationId?._id,
    );

    if (!findRelevantItem) {
      ToastMessage.error('Item not found, try scanning again');
      return;
    }

    setProcessing(true);

    const payload = {
      allocationId: findRelevantItem?.allocationId?._id,
      unpack: true,
    };

    try {
      await PackagingService.packToteItem(data?.[key]?._id, payload);
      resetScan();

      if (packageNumber !== null) {
        setPackages(packages => {
          packages[packageNumber].items = packages[packageNumber].items.map(packItem => {
            if (packingItemKey(packItem) === packingItemKey(item)) {
              packItem.qty -= 1;
            }
            return packItem;
          });
          return packages;
        });
      }

      setItems(items =>
        items.map(item => {
          if (item._id === findRelevantItem._id) {
            return {
              ...item,
              status: 'ready_to_pack',
            };
          }
          return item;
        }),
      );
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  const askToPrint = async (toPrint, fileName = 'document.pdf') => {
    if (toPrint) {
      if (
        currentWarehouse?.shipnodeConfigurations?.shipnode &&
        currentStation?.computerId &&
        currentStation?.printerId
      ) {
        // do shipnode printing
        try {
          const body = {
            message: new Date().toString(),
            computer_id: currentStation?.computerId,
            printer_id: currentStation?.printerId,
            url: toPrint,
          };
          await PackagingService.printLabel(body);
        } catch (error) {
          ToastMessage.apiError(error);
        }
      } else {
        downloadPDF(toPrint, fileName);
      }
    }
  };

  const print = useCallback(async () => {
    if (CommonUtility.isValidArray(packages)) {
      for (let i = 0; i < packages.length; i += 1) {
        const label = packages?.[i]?.label;
        const toPrint = label?.url;
        const fileName = formatTotePackageLabelFileName(
          data?.[key]?.order?.marketplace_order_display_id,
          data?.[key]?.toteNumber,
          i + 1,
        );
        await askToPrint(toPrint, fileName);
      }
    }
  }, [packages, data?.[key]]);

  const refundLabel = async () => {
    if (!CommonUtility.isValidArray(labelToRefund)) {
      ToastMessage.error(ErrorMessageConstant.defaultAPIError);
      return;
    }

    try {
      setPopupLoading(true);
      for (let i = 0; i < labelToRefund.length; i += 1) {
        const labelRefunding = labelToRefund[i];
        await PackagingService.refundLabel(labelRefunding?._id);

        if (wasPackageLabel) {
          setPackages(packages =>
            packages.map(pack => {
              if (pack.label?._id === labelRefunding?._id) {
                pack.label = null;
              }
              return pack;
            }),
          );
        }
      }
      ToastMessage.success('Label refunded successfully');
      if (!wasPackageLabel) {
        refresh();
      }
      setLabelToRefund(null);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setPopupLoading(false);
    }
  };

  const closeRefund = () => {
    setLabelToRefund(null);
    setAddPackagePopup(false);

    if (CommonUtility.isValidArray(cachedPackage)) {
      setPackages(cachedPackage);
      setCachedPackage(null);
    }
  };

  const closePopup = () => {
    setCreateNewLabelPopup(false);
    setOrderToUse(null);
    setContainerItems(null);
    setLabelAllocations(null);
  };

  const addOneEmptyPackage = () => {
    setActivePackage(packages.length);
    setPackages(packages => [...packages, { items: [] }]);
  };

  const increaseCount = (item, newPackIndex) => {
    setPackages(packages =>
      update(packages, {
        [newPackIndex]: {
          items: {
            $apply: items => {
              const findNewItemIndex = items?.findIndex(
                x => packingItemKey(x) === packingItemKey(item),
              );

              if (findNewItemIndex > -1) {
                return update(items, {
                  [findNewItemIndex]: { qty: { $apply: qty => qty + 1 } },
                });
              }
              return update(items, {
                $push: [packageRow(item)],
              });
            },
          },
        },
        [item.packageIndex]: {
          items: {
            $apply: items =>
              items.map(packItem =>
                packingItemKey(packItem) === packingItemKey(item)
                  ? { ...packItem, qty: packItem.qty - 1 }
                  : packItem,
              ),
          },
        },
      }),
    );
  };

  const packedItems = useMemo(() => {
    if (CommonUtility.isValidArray(items)) {
      return items?.filter(x =>
        isPallet ? x.status === 'shipped' : x.status === 'packed',
      );
    }
    return [];
  }, [items, isPallet]);

  const packagesWithPackedItems = useMemo(
    () => assignPackageItems(packages, packedItems).packages,
    [packages, packedItems],
  );

  const hasPackageLabelMismatch = useMemo(
    () =>
      packagesWithPackedItems.some(
        pack => pack.label && !doesPackageLabelMatchPackedItems(pack),
      ),
    [packagesWithPackedItems],
  );

  const openLabelGenerationForPackage = packageIndex => {
    const pack = packagesWithPackedItems[packageIndex];
    if (pack?.identityError) {
      ToastMessage.error(pack.identityError);
      return;
    }
    setCreateNewLabelPopup(true);
    setOrderToUse(data?.[key]?.order);
    setContainerItems(pack?.items);
    setLabelAllocations(packagesWithPackedItems[packageIndex]?.packedItems);
    setLabelForPackage(packageIndex);
  };

  const onLabelGenerated = (packageIndex, label) => {
    setPackages(packages => {
      packages[packageIndex].label = {
        ...label,
        carrier: label.provider,
        items: packages[packageIndex].items,
        _id: label.label_id,
      };
      return [...packages];
    });
  };

  const onPalletLabelGenerated = (allocationId, label) => {
    setItems(items => {
      const newItems = items.map(item => {
        if (item?.allocationId?._id === allocationId) {
          return {
            ...item,
            shipping: {
              ...(item.shpping || {}),
              labels: [label],
            },
          };
        }
        return item;
      });
      return newItems;
    });
  };

  const allItemsPacked = packedItems.length === items.length && !isPallet;

  useEffect(() => {
    if (
      allItemsPacked &&
      currentWarehouse?.packingSettings?.showBarcodeOnPacking &&
      items.length
    ) {
      setShowBarcodeModal(true);
    }
  }, [allItemsPacked, currentWarehouse, items]);

  const refundPackageLabel = label => {
    setLabelToRefund(previousLabels => [...(previousLabels || []), label]);
    setWasPackageLabel(true);
  };

  const moveBack = () => {
    navigate('/app/fulfill/packing/list/to-pack');
  };

  const addPackage = () => {
    // if there's a label to void else create new package
    if (CommonUtility.isValidArray(data?.tote?.shipping?.labels) && !allItemsPacked) {
      setLabelToRefund(data?.tote?.shipping?.labels);
      setAddPackagePopup(true);
    } else {
      setCachedPackage(JSON.parse(JSON.stringify(packages)));
      addOneEmptyPackage();
    }
  };

  const onCancelItem = allocationId => {
    const newItems = items.filter(x => x.allocationId?._id !== allocationId);

    if (newItems.find(x => x.isCancelled)) {
      setItems(items => items.filter(x => x.allocationId?._id !== allocationId));
    } else if (!CommonUtility.isValidArray(newItems)) {
      moveBack();
    } else {
      refresh();
    }
  };

  const allCancelled = useMemo(() => items.every(x => x.isCancelled), [items]);

  const onCloseConfirmationPopup = () => {
    setOpenConfirmationPopup(false);
    setConfirmationAllocation(null);
  };

  const openPackageCorrection = (pack, index) => {
    setPackageCorrectionContext({ pack, index });
    setPackageCorrectionOpen(true);
  };

  const closePackageCorrection = () => {
    setPackageCorrectionOpen(false);
    setPackageCorrectionContext(null);
  };

  const editAddress = () => {
    setOpenEditAddressPopup(true);
  };

  const closeAddressPopup = () => {
    setOpenEditAddressPopup(false);
  };

  const isOpenContainer = data?.[key]?.status === 'open';
  const isContainerError = error?.status === 400;

  useAppHeaderLead(
    () => (
      <PackingDetailHeaderLead
        data={data?.[key]}
        backOnly={isOpenContainer || isContainerError}
      />
    ),
    [data, key, isOpenContainer, isContainerError],
  );

  useHeaderActions(() => {
    if (isOpenContainer || isContainerError || !data?.[key]) return null;
    return (
      <PackingDetailHeaderActions
        addPackage={addPackage}
        allPacked={allPacked}
        fulfillTote={onFulfillClicked}
        isPallet={isPallet}
        packages={packages}
        shipped={shipped}
        showActions={!allCancelled}
        editAddress={editAddress}
      />
    );
  }, [
    isOpenContainer,
    isContainerError,
    data,
    key,
    addPackage,
    allPacked,
    isPallet,
    packages,
    shipped,
    allCancelled,
    editAddress,
    onFulfillClicked,
  ]);

  if (isOpenContainer) {
    return (
      <OpenContainerBody>
        <RectangleDashed size={80} />
        <HeaderText className="my-4">This container has no items yet</HeaderText>
        <HoverBorderButton onClick={moveBack}>Back to List</HoverBorderButton>
      </OpenContainerBody>
    );
  }

  if (isContainerError) {
    return (
      <OpenContainerBody>
        <RectangleDashed size={80} />
        <HeaderText className="my-4">
          {error?.error?.message || 'Container is not ready to pack'}
        </HeaderText>
        <HoverBorderButton onClick={moveBack}>Back to List</HoverBorderButton>
      </OpenContainerBody>
    );
  }

  return (
    <BodyContainer>
      {!allCancelled ? (
        <div className="d-flex mt-4 w-100">
          {allItemsPacked && (
            <ToteContainer className="mr-3">
              <ToteTextContainer className="d-flex align-items-center">
                <Tray className="icon rotate" size={20} />
                <h3 className="my-1">Tote</h3>
                <Pill className="mt-2">
                  <Barcode className="rotate" />
                  <span>{data?.[key]?.toteNumber}</span>
                </Pill>
              </ToteTextContainer>
            </ToteContainer>
          )}
          <div className="row w-100" ref={bodyRef}>
            <PackingDetailItems
              confirmFulfillItem={confirmFulfillItem}
              data={data?.[key]}
              fulfillItem={fulfillItem}
              isConfirmationRequired={isUnSorted}
              isPallet={isPallet}
              items={items}
              loading={loading || processing}
              packItem={packItem}
            />
            <PackingDetailPacks
              activePackage={activePackage}
              askToPrint={askToPrint}
              data={data[key]}
              fileName={`${data?.[key]?.order?.marketplace_order_display_id} - ${data?.[key]?.toteNumber}`}
              hasShipNode={currentWarehouse?.shipnodeConfigurations?.shipnode}
              increaseCount={increaseCount}
              isPallet={isPallet}
              items={items}
              loading={loading || processing}
              onOpenPackageCorrection={openPackageCorrection}
              openLabelGenerationForPackage={openLabelGenerationForPackage}
              packages={packages}
              packedItems={packedItems}
              printItem={printItem}
              setActivePackage={setActivePackage}
              setLabelToRefund={refundPackageLabel}
              setPackages={setPackages}
              unPackItem={unPackItem}
            />
          </div>
        </div>
      ) : loading ? (
        <PackagingDetailPlaceholder />
      ) : (
        <></>
      )}
      <CancelledItems
        isPallet={isPallet}
        items={cancelledItems}
        onCancelItem={onCancelItem}
        allItemsPacked={allItemsPacked}
      />
      <ConfirmationPopup
        open={labelToRefund}
        title="Void Label"
        message={
          <div>
            {addPackagePopup && (
              <p>
                Adding a new package requires us to void previous label by refunding them.
              </p>
            )}
            <span>Are you sure you want to void this&nbsp;</span>
            <a target="_blank" href={labelToRefund?.[0]?.url} rel="noreferrer">
              label?
            </a>
          </div>
        }
        onConfirm={refundLabel}
        onClose={closeRefund}
        closeText="Close"
        loading={popupLoading}
      />
      <CreateNewLabelPopup
        data={data[key]}
        items={containerItems}
        labelForPackage={labelForPackage}
        onClose={closePopup}
        onLabelGenerated={onLabelGenerated}
        open={createNewLabelPopup}
        orderToUse={orderToUse}
        refresh={refresh}
        warehouseId={data[key]?.warehouseId}
        labelAllocations={labelAllocations}
      />
      <PackagingConfirmationPopup
        closeAndFulfill={fulfillTote}
        currentAllocation={confirmationAllocation}
        fulfillItem={fulfillItem}
        onClose={onCloseConfirmationPopup}
        onLabelGenerated={onLabelGenerated}
        onPalletLabelGenerated={onPalletLabelGenerated}
        open={confirmationPopup}
        order={data?.[key]?.order}
        packages={packages}
        processing={processing}
        toteId={data[key]?._id}
        warehouseId={data[key]?.warehouseId}
      />
      <PackageCorrectionPopup
        open={
          packageCorrectionOpen && CommonUtility.isValidObject(packageCorrectionContext)
        }
        onClose={closePackageCorrection}
        onLabelGenerated={onLabelGenerated}
        order={data?.[key]?.order}
        packageIndex={packageCorrectionContext?.index ?? 0}
        pack={packageCorrectionContext?.pack}
        processing={processing}
        toteId={data[key]?._id}
        warehouseId={data[key]?.warehouseId}
      />
      <EditAddressPopup
        orderId={data?.[key]?.order?._id}
        address={data?.[key]?.order?.shipping_address}
        open={openEditAddressPopup}
        onClose={closeAddressPopup}
        refresh={refresh}
      />
      <ToteBarcodePopup
        toteNumber={data?.[key]?.toteNumber}
        isOpen={showBarcodeModal}
        hasPackageLabelMismatch={hasPackageLabelMismatch}
        onClose={() => setShowBarcodeModal(false)}
      />
    </BodyContainer>
  );
}
